/**
 * AudioManager — procedural Web Audio tuned for a microbial-cell vibe.
 *
 * Design choices:
 *  - Silent until the first user gesture (required by browser autoplay
 *    policy). Any pointerdown / keydown engages audio at the saved volume;
 *    respects the muted setting.
 *  - All SFX go through lowpass filters and use sine/triangle waves, so
 *    they feel soft and organic rather than harsh/synth.
 *  - An ambient pad (three slow detuned drones + filter LFO) plus sparse
 *    pentatonic pings provide focus music that doesn't demand attention.
 *  - No sample files — everything is synthesized.
 *
 * EventBus routes:
 *   - 'audio:pop'         — pitched absorption squish
 *   - 'audio:cue'         — named one-shot (damage, event, dash, ...)
 *   - 'audio:setVolume'   — user moved slider (engages audio + unmutes)
 *   - 'audio:setMuted'    — user toggled mute (engages audio)
 */

import { bus } from '@/core/EventBus';
import { saveManager } from '@/core/SaveManager';

/** Backing-track peak gain. Kept low so SFX cut through. */
const TRACK_PEAK = 0.22;
/** Crossfade times when switching tracks. Music swap should feel slow
 *  and atmospheric, not abrupt. */
const TRACK_FADE_IN = 2.5;
const TRACK_FADE_OUT = 1.5;

type TrackId = 'main' | 'multicell' | 'cambrian';

const TRACK_SRC: Record<TrackId, string> = {
  main: '/Pattern_of_the_Bloom.mp3',
  multicell: '/Cellular_Bloom.mp3',
  cambrian: '/Beneath_The_Liquid_Lens.mp3',
};

interface MusicChannel {
  el: HTMLAudioElement;
  source: MediaElementAudioSourceNode | null;
  gain: GainNode;
}

export class AudioManager {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private userEngaged = false;
  private currentTrackId: TrackId | null = null;
  /** Most recently requested track; applied on first user gesture. */
  private pendingTrackId: TrackId | null = null;

  // Per-track audio routing. Lazy-allocated on first play of each track
  // so we don't pay decode cost for tracks the player never reaches.
  private channels: Partial<Record<TrackId, MusicChannel>> = {};

  constructor() {
    // First gesture anywhere on the page engages audio (music + SFX).
    // Using pointerdown on the capture phase so it fires even if the event
    // is stopPropagation'd deeper in the tree (ability pad, HUD buttons).
    const resume = () => {
      this.engage();
      this.applyVolume();
    };
    window.addEventListener('pointerdown', resume, { once: true, capture: true });
    window.addEventListener('keydown', resume, { once: true, capture: true });
    window.addEventListener('touchstart', resume, { once: true, capture: true });

    bus.on('audio:pop', ({ pitch, volume }) => this.pop(pitch, volume ?? 0.3));
    bus.on('audio:cue', ({ id, volume }) => this.cue(id, volume ?? 0.35));

    bus.on('audio:setVolume', ({ value }) => {
      const meta = saveManager.getMeta();
      meta.settings.volume = Math.max(0, Math.min(1, value));
      meta.settings.muted = false; // moving the slider is intent to hear sound
      this.engage();
      this.applyVolume();
    });
    bus.on('audio:setMuted', ({ muted }) => {
      saveManager.getMeta().settings.muted = muted;
      this.engage();
      this.applyVolume();
    });
    bus.on('music:setTrack', ({ id }) => {
      this.pendingTrackId = id;
      if (this.userEngaged) this.applyTrack();
    });
  }

  private ensure(): AudioContext | null {
    if (this.ctx) return this.ctx;
    try {
      const Ctx: typeof AudioContext =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return null;
      this.ctx = new Ctx();
      this.master = this.ctx.createGain();
      // Start at 0 — applyVolume() ramps to the saved volume once engaged.
      this.master.gain.value = 0;
      this.master.connect(this.ctx.destination);
    } catch { /* ignore */ }
    // Some browsers (Safari, mobile Chrome) start the context in 'suspended'
    // state and only allow resume() from inside a user-gesture handler.
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
    return this.ctx;
  }

  private engage(): void {
    this.ensure();
    if (this.userEngaged) return;
    this.userEngaged = true;
    // If a stage already requested a track before the gesture, start
    // it now. Otherwise stay silent until a stage tells us what to play.
    if (this.pendingTrackId) this.applyTrack();
  }

  /**
   * Switch to the pending track with a crossfade. Each stage has its
   * own track id ('main' for Phase 1, 'multicell' for Phase 2) — when
   * the id changes we fade the previous channel out and fade the new
   * one in over a couple of seconds so the swap feels atmospheric.
   */
  private applyTrack(): void {
    const next = this.pendingTrackId;
    if (!next) return;
    if (next === this.currentTrackId) return;
    const prev = this.currentTrackId;
    this.startTrack(next);
    if (prev) this.fadeOutTrack(prev);
    this.currentTrackId = next;
  }

  private applyVolume(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const muted = saveManager.getMeta().settings.muted;
    const target = (this.userEngaged && !muted) ? 1 : 0;
    const now = ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.linearRampToValueAtTime(target, now + 0.15);
  }

  setVolume(v: number): void {
    bus.emit('audio:setVolume', { value: v });
  }
  setMuted(m: boolean): void {
    bus.emit('audio:setMuted', { muted: m });
  }

  // --- music: per-track mp3 backing channels ------------------------

  private getChannel(id: TrackId): MusicChannel | null {
    const ctx = this.ensure();
    if (!ctx || !this.master) return null;
    let ch = this.channels[id];
    if (ch) return ch;
    const el = new Audio(TRACK_SRC[id]);
    el.loop = true;
    el.preload = 'auto';
    el.crossOrigin = 'anonymous';
    let source: MediaElementAudioSourceNode | null = null;
    try {
      source = ctx.createMediaElementSource(el);
    } catch {
      // Some browsers throw if the element was already routed; in
      // that case the element falls back to its own audio path
      // and we lose the master-volume route. Acceptable.
    }
    const gain = ctx.createGain();
    gain.gain.value = 0;
    if (source) source.connect(gain).connect(this.master);
    ch = { el, source, gain };
    this.channels[id] = ch;
    return ch;
  }

  private startTrack(id: TrackId): void {
    const ctx = this.ensure();
    if (!ctx) return;
    const ch = this.getChannel(id);
    if (!ch) return;
    const now = ctx.currentTime;
    ch.gain.gain.cancelScheduledValues(now);
    ch.gain.gain.setValueAtTime(Math.max(0.0001, ch.gain.gain.value), now);
    ch.gain.gain.linearRampToValueAtTime(TRACK_PEAK, now + TRACK_FADE_IN);
    ch.el.play().catch(() => {
      // play() may reject if there has been no gesture; the engage()
      // path retries on first interaction. Ignore quietly.
    });
  }

  private fadeOutTrack(id: TrackId): void {
    const ctx = this.ctx;
    const ch = this.channels[id];
    if (!ctx || !ch) return;
    const now = ctx.currentTime;
    ch.gain.gain.cancelScheduledValues(now);
    ch.gain.gain.setValueAtTime(Math.max(0.0001, ch.gain.gain.value), now);
    ch.gain.gain.linearRampToValueAtTime(0, now + TRACK_FADE_OUT);
    // Pause the underlying element after the fade completes so we
    // stop streaming a silent track. Skip the pause if the player
    // switches back to this track before the fade finishes.
    setTimeout(() => {
      if (this.currentTrackId !== id) ch.el.pause();
    }, (TRACK_FADE_OUT + 0.2) * 1000);
  }

  // --- SFX -----------------------------------------------------------

  private pop(pitch: number, volume: number): void {
    const ctx = this.ensure();
    if (!ctx || !this.master || !this.userEngaged) return;
    const now = ctx.currentTime;

    const o = ctx.createOscillator();
    const g = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2400, now);
    filter.frequency.exponentialRampToValueAtTime(800, now + 0.2);
    filter.Q.value = 1.1;

    o.type = 'sine';
    const base = 160 + pitch * 220;
    o.frequency.setValueAtTime(base * 1.5, now);
    o.frequency.exponentialRampToValueAtTime(base, now + 0.1);

    g.gain.setValueAtTime(0.0001, now);
    g.gain.linearRampToValueAtTime(volume * 0.55, now + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0005, now + 0.22);

    o.connect(filter).connect(g).connect(this.master);
    o.start(now);
    o.stop(now + 0.26);
  }

  private cue(id: string, volume: number): void {
    const ctx = this.ensure();
    if (!ctx || !this.master || !this.userEngaged) return;
    const now = ctx.currentTime;

    // Per-cue builder: osc -> lowpass -> gain -> master
    const buildVoice = (cutoff: number, Q = 0.8) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      const f = ctx.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.value = cutoff;
      f.Q.value = Q;
      g.gain.setValueAtTime(0.0001, now);
      o.connect(f).connect(g).connect(this.master!);
      return { o, g, f };
    };

    switch (id) {
      case 'damage': {
        // Muffled organic thud — low sine with steep lowpass.
        const { o, g } = buildVoice(380, 1.4);
        o.type = 'sine';
        o.frequency.setValueAtTime(130, now);
        o.frequency.exponentialRampToValueAtTime(50, now + 0.35);
        g.gain.linearRampToValueAtTime(volume * 0.7, now + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.42);
        o.start(now); o.stop(now + 0.45);
        break;
      }
      case 'dash': {
        // Soft woosh — triangle down-sweep through an opening filter.
        const { o, g, f } = buildVoice(1800);
        o.type = 'triangle';
        o.frequency.setValueAtTime(520, now);
        o.frequency.exponentialRampToValueAtTime(160, now + 0.2);
        f.frequency.setValueAtTime(2200, now);
        f.frequency.exponentialRampToValueAtTime(500, now + 0.2);
        g.gain.linearRampToValueAtTime(volume * 0.5, now + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
        o.start(now); o.stop(now + 0.25);
        break;
      }
      case 'pulse': {
        // Membrane ping — sine up-sweep, bandpass-ish with high Q for shimmer.
        const { o, g } = buildVoice(2800, 2);
        o.type = 'sine';
        o.frequency.setValueAtTime(440, now);
        o.frequency.exponentialRampToValueAtTime(1320, now + 0.14);
        g.gain.linearRampToValueAtTime(volume * 0.45, now + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
        o.start(now); o.stop(now + 0.25);
        break;
      }
      case 'event': {
        // Awakening tone — slow sine glide, warm and sustained.
        const { o, g } = buildVoice(1100);
        o.type = 'sine';
        o.frequency.setValueAtTime(330, now);
        o.frequency.linearRampToValueAtTime(495, now + 0.5);
        g.gain.linearRampToValueAtTime(volume * 0.4, now + 0.05);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.7);
        o.start(now); o.stop(now + 0.75);
        break;
      }
      case 'milestone': {
        // Soft pentatonic arpeggio (A-C#-E pattern, triangle + lowpass).
        const freqs = [440, 554.37, 659.25];
        freqs.forEach((f, i) => {
          const start = now + i * 0.1;
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          filter.type = 'lowpass';
          filter.frequency.value = 2000;
          filter.Q.value = 0.8;
          o.type = 'triangle';
          o.frequency.setValueAtTime(f, start);
          g.gain.setValueAtTime(0.0001, start);
          g.gain.linearRampToValueAtTime(volume * 0.32, start + 0.02);
          g.gain.exponentialRampToValueAtTime(0.0001, start + 0.55);
          o.connect(filter).connect(g).connect(this.master!);
          o.start(start);
          o.stop(start + 0.6);
        });
        break;
      }
      case 'satelliteSpawn': {
        // Tiny bubble — sine down-chirp, short and quiet.
        const { o, g } = buildVoice(1600);
        o.type = 'sine';
        o.frequency.setValueAtTime(900, now);
        o.frequency.exponentialRampToValueAtTime(520, now + 0.12);
        g.gain.linearRampToValueAtTime(volume * 0.18, now + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
        o.start(now); o.stop(now + 0.18);
        break;
      }
      case 'satelliteClear': {
        // Light upward chirp — triangle through soft lowpass.
        const { o, g } = buildVoice(2000);
        o.type = 'triangle';
        o.frequency.setValueAtTime(523, now);
        o.frequency.linearRampToValueAtTime(784, now + 0.1);
        g.gain.linearRampToValueAtTime(volume * 0.28, now + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
        o.start(now); o.stop(now + 0.22);
        break;
      }
      case 'bloomCleared': {
        // Four-note shimmer: D-F#-A-D, triangle wave, warm lowpass.
        const freqs = [523.25, 659.25, 783.99, 1046.5];
        freqs.forEach((f, i) => {
          const start = now + i * 0.06;
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          filter.type = 'lowpass';
          filter.frequency.value = 2400;
          filter.Q.value = 0.9;
          o.type = 'triangle';
          o.frequency.setValueAtTime(f, start);
          g.gain.setValueAtTime(0.0001, start);
          g.gain.linearRampToValueAtTime(volume * 0.4, start + 0.015);
          g.gain.exponentialRampToValueAtTime(0.0001, start + 0.45);
          o.connect(filter).connect(g).connect(this.master!);
          o.start(start);
          o.stop(start + 0.48);
        });
        break;
      }
      case 'codex': {
        // Gentle bell tone, sustained.
        const { o, g } = buildVoice(2400);
        o.type = 'sine';
        o.frequency.setValueAtTime(784, now);
        g.gain.linearRampToValueAtTime(volume * 0.28, now + 0.04);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
        o.start(now); o.stop(now + 0.55);
        break;
      }
      case 'bomb': {
        // Thick sweep down: sawtooth through bandpass, dropping an octave+.
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = 900;
        filter.Q.value = 1.2;
        o.type = 'sawtooth';
        o.frequency.setValueAtTime(220, now);
        o.frequency.exponentialRampToValueAtTime(40, now + 0.55);
        g.gain.setValueAtTime(0.0001, now);
        g.gain.linearRampToValueAtTime(volume * 1.1, now + 0.015);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.7);
        o.connect(filter).connect(g).connect(this.master!);
        o.start(now); o.stop(now + 0.75);
        // noise burst on top for percussive edge
        const buf = ctx.createBuffer(1, ctx.sampleRate * 0.25, ctx.sampleRate);
        const data = buf.getChannelData(0);
        for (let i = 0; i < data.length; i++) {
          data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
        }
        const n = ctx.createBufferSource();
        n.buffer = buf;
        const ng = ctx.createGain();
        ng.gain.value = volume * 0.6;
        n.connect(ng).connect(this.master!);
        n.start(now);
        break;
      }
      case 'shield': {
        // Rising crystalline shimmer — two sine tones in a fifth.
        const freqs = [523, 784];
        freqs.forEach((f) => {
          const { o, g } = buildVoice(4000);
          o.type = 'sine';
          o.frequency.setValueAtTime(f * 0.75, now);
          o.frequency.linearRampToValueAtTime(f, now + 0.2);
          g.gain.linearRampToValueAtTime(volume * 0.3, now + 0.03);
          g.gain.exponentialRampToValueAtTime(0.0001, now + 0.55);
          o.start(now); o.stop(now + 0.6);
        });
        break;
      }
    }
  }
}
